import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';

export default class Scroller {
  constructor(element) {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

    this.options = {
      hasSkew: false,
      hasPinItems: false,
    };

    this.element = element;

    this.setOptions();
    this.init();
  }

  init() {
    // Création du smoother
    this.smoother = ScrollSmoother.create({
      smooth: 1.5,
      effects: true,
      smoothTouch: 0.1,
      onUpdate: this.onUpdateScroll.bind(this),
      onStop: this.onStopScroll.bind(this),
      ease: 'expo.out',
    });

    // Gestion des clics sur les ancres
    this.handleAnchorLinks();

    // Scroll automatique si URL contient déjà un hash
    window.addEventListener('load', () => {
      const hash = window.location.hash;
      if (hash) this.scrollToHash(hash);
    });
  }

  onUpdateScroll(self) {
    if (this.options.hasSkew) this.updateSkew(self);
  }

  onStopScroll(self) {
    if (this.options.hasSkew) this.stopSkew(self);
  }

  /** Gestion des clics sur les liens avec hash */
  handleAnchorLinks() {
    document.querySelectorAll('a[href*="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const url = new URL(anchor.href, location.href);
        const hash = url.hash;

        if (!hash || hash === '#') return;

        // Même page ?
        if (url.pathname === location.pathname) {
          const target = document.querySelector(hash);
          if (target && this.smoother) {
            e.preventDefault();
            this.scrollToHash(hash);
            history.pushState(null, null, hash);
          }
        } else {
          // Lien vers une autre page → navigation normale
          e.preventDefault();
          window.location.href = url.pathname + hash;
        }
      });
    });
  }

  /** Scroll vers un hash avec offset header */
  scrollToHash(hash) {
    const target = document.querySelector(hash);
    if (!target || !this.smoother) return;

    const header = document.querySelector('header');
    const headerHeight = header ? header.offsetHeight : 0;

    // Utiliser offset pour obtenir position exacte dans le smoother
    let targetY;
    if (typeof this.smoother.offset === 'function') {
      targetY = this.smoother.offset(target, 'top', true) - headerHeight;
      this.smoother.scrollTo(targetY, true);
    } else {
      // fallback si offset non dispo
      this.smoother.scrollTo(target, true);
    }
  }

  //================== SKEW ==================
  initSkew() {
    this.skewSetter = gsap.quickTo('img', 'skewY');
  }

  updateSkew(self) {
    const velocity = self.getVelocity();
    const force = 5;
    let skew = gsap.utils.mapRange(-1000, 1000, -force, force, velocity);
    skew = gsap.utils.clamp(-force, force, skew);
    this.skewSetter(skew);
  }

  stopSkew() {
    this.skewSetter(0);
  }

  //================== PIN ==================
  initPins() {
    const pinnedItems = this.element.querySelectorAll('.js-pinned');
    pinnedItems.forEach((pinnedItem) => {
      ScrollTrigger.create({
        pin: pinnedItem,
        trigger: pinnedItem.parentElement,
        start: 'center center',
        end: '80% center',
      });
    });
  }

  //================== HORIZONTAL SECTIONS ==================
  initHoriz() {
    const sectionsHoriz = this.element.querySelectorAll('.js-horiz');
    sectionsHoriz.forEach((sectionHoriz) => {
      const panels = sectionHoriz.querySelectorAll('.js-panel');
      const nbPanels = panels.length - 1;
      const buffer = 200;

      gsap.to(panels, {
        xPercent: -100 * nbPanels,
        ease: 'none',
        scrollTrigger: {
          pin: true,
          trigger: sectionHoriz,
          scrub: 1,
          snap: 1 / nbPanels,
          end: () => '+=' + (sectionHoriz.offsetWidth + buffer),
        },
      });
    });
  }

  //================== OPTIONS ==================
  setOptions() {
    if ('skew' in this.element.dataset) {
      this.options.hasSkew = true;
      this.initSkew();
    }
    if ('pinItems' in this.element.dataset) {
      this.options.hasPinItems = true;
      this.initPins();
    }
    if (this.element.querySelector('.js-horiz')) {
      this.initHoriz();
    }
  }
}
